import { DownOutlined } from "@ant-design/icons";
import { useReactiveVar } from "@apollo/client";
import { Button, DatePicker, Divider, Dropdown, Empty, Input, Space } from "antd";
import dayjs from "dayjs";
import fileDownload from "js-file-download";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./Warranty.scss";
import WarrantyPrintButton from "./components/WarrantyPrintButton";
import { createColumns } from "./content/warrantyColumns";
import { StatusItems, StatusTypes } from "./content/warrantyContent";
import Spinner from "../../../components/layout/spinner/Spinner";
import NoPermission from "../../../components/others/NoPermission";
import ContentTitle from "../../../components/ui/ContentTitle";
import MarketDropdown from "../../../components/ui/MarketDropdown";
import TableCustom from "../../../components/ui/TableCustom";
import { useGetWarrantyListQuery } from "../../../graphql/operations/clubApp/warranty/get-warranty-list";
import { useUpdateWarrantyStatusMutation } from "../../../graphql/operations/clubApp/warranty/update-warranty-status";
import { WarrantyListResponse } from "../../../graphql/operations/@types";
import { useGetUserProgramsByServiceQuery } from "../../../graphql/operations/get-user-program-by-service";
import { __currentUser__, __jwtToken__, __projectKey__, } from "../../../graphql/policies";
import { MarketType, ServiceTypes } from "../../../types/types";
import { setFeatureMarketLanguage } from "../../../i18n/featureLanguageSession"
import { apiCall } from "../../../utils/apiCall";
import getInitialMarket from "../../../utils/getInitialMarket";
import GeneralModal from "../../../utils/GeneralModal";
import { renderDecodedId } from "../../../utils/renderDecodedId";

interface WarrantyProps {
  setSelectedService: Dispatch<SetStateAction<ServiceTypes>>;
  setSelectSubItem: Dispatch<SetStateAction<string | undefined>>;
}

const { Search } = Input;
const { RangePicker } = DatePicker;

const Warranty = ({ setSelectedService, setSelectSubItem }: WarrantyProps) => {
  const currentUser = useReactiveVar(__currentUser__);
  const jwtToken = useReactiveVar(__jwtToken__);
  const projectKey = useReactiveVar(__projectKey__);
  const navigate = useNavigate();
  const [selectStatus, setSelectStatus] = useState<StatusTypes>(StatusTypes.Awaiting);
  const [selectedMarket, setSelectedMarket] = useState<MarketType>(
    getInitialMarket()
  );
  const [dateRange, setDateRange] = useState<[string, string]>([dayjs().subtract(3, "month").format("YYYY-MM-DD"), dayjs().format("YYYY-MM-DD")]);
  const [selectData, setSelectData] = useState<number[]>([]);
  const [searchText, setSearchText] = useState("");
  const [searchBoxText, setSearchBoxText] = useState("");
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const { t: trans } = useTranslation(["clubApp", "common"]);

  const { data: userPrograms, loading: userProgramsLoading } =
    useGetUserProgramsByServiceQuery({
      variables: {
        userId: parseInt(renderDecodedId(currentUser?.id)),
        serviceId: 2,
      },
    });

  const { data, refetch: warrantyListRefetch } = useGetWarrantyListQuery({
    variables: {
      market: selectedMarket,
      startDate: dateRange?.[0] ?? "",
      endDate: dateRange?.[1] ?? "",
      status: selectStatus,
      searchText: searchText ?? "",
    },
    fetchPolicy: "network-only",
  });

  const warrantyList = data?.warrantyList ?? [];

  const [updateStatus] = useUpdateWarrantyStatusMutation();

  const hasSearchCondition =
    searchText !== "" ||
    (dateRange && dateRange[0] !== "" && dateRange[1] !== "");

  const isAllSelect =
    warrantyList.length > 0 &&
    warrantyList.every((item) => selectData.includes(item.applicationId));

  const onSearch = (value: string) => {
    setSearchText(value);
  };

  const handleDetailClick = (applicationId: number) => {
    navigate(`/club-app/warranty/${applicationId}/detail`);
  };

  const handleCompleteClick = async () => {
    setShowCompleteModal(true)
  };

  const handleCompleteClose = async () => {
    setShowCompleteModal(false);
  };

  const handleConfirmClick = async () => {
    try {
      await updateStatus({
        variables: {
          applicationId: selectData,
          status: StatusTypes.Complete,
          updateBy: currentUser?.userName!,
        }
      });

      setShowCompleteModal(false);
      setSelectData([]);
      await warrantyListRefetch();
    } catch (e) { }
  };

  const handleExportClick = async () => {
    const header = {
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        "X-Project-Id": "AF",
        "X-Authorization-Provider": "Anonymous",
        "X-Project-Key": projectKey,
        "Content-Type": "application/json",
      },
      responseType: "blob",
    };
    try {
      const data = await apiCall({
        method: "POST",
        header: header,
        endPoint: process.env.REACT_APP_ADMIN_API + "Af/GetWarranties",
        payload: {
          Market: selectedMarket,
          ApplicationIds: selectData
        }
      });
      fileDownload(data, `WarrantyReplacementData${dayjs().format("YYYYMMDD")}.csv`);
    } catch (e) { }
  };

  const handleCheckBoxTitleChange = (e: any) => {
    if (e.target.checked) {
      setSelectData(data?.warrantyList.map((item) => item.applicationId) ?? []);
    } else {
      setSelectData([]);
    }
  };

  const handleCheckBoxRowChange = (applicationId: number) => {
    setSelectData((prev) => {
      if (prev.includes(applicationId)) {
        return prev.filter((item) => item !== applicationId);
      } else {
        return [...prev, applicationId];
      }
    });
  };

  const handleResetFiltersDates = () => {
    setDateRange([dayjs().subtract(3, "month").format("YYYY-MM-DD"), dayjs().format("YYYY-MM-DD")]);
  };

  const handleResetFiltersToday = () => {
    const today = dayjs().format("YYYY-MM-DD");
    setDateRange([today, today]);
    setSearchText("");
  };

  useEffect(() => {
    setSelectedService(ServiceTypes.CLUB_APP);
    setSelectSubItem("Warranty");
  }, []);

  const statusMenuItems = StatusItems.map(item => ({
    ...item,
    label: trans(`clubApp:warranty.${item.key.replace(' ', '')}`, {
      defaultValue: item.label,
    }),
  }));

  useEffect(() => {
    if (currentUser?.afUserMarkets && currentUser?.afUserMarkets[0]?.market) {
      if (currentUser?.afUserMarkets.length === 1) {
        setSelectedMarket(currentUser?.afUserMarkets[0]?.market as MarketType);
      } else {
        setSelectedMarket("JP");
      }

      const savedFilters = localStorage.getItem("warrantyFilters");
      if (savedFilters) {
        const parsedFilters = JSON.parse(savedFilters);
        if (parsedFilters.status)
          setSelectStatus(parsedFilters.status);

        if (parsedFilters.market)
          setSelectedMarket(parsedFilters.market);

        if (parsedFilters.date)
          setDateRange(parsedFilters.date);

        if (parsedFilters.acno) {
          setSearchText(parsedFilters.acno);
          setSearchBoxText(parsedFilters.acno);
        }
      }
    }
  }, [currentUser]);

  useEffect(() => {
    // i18n
    setFeatureMarketLanguage("warranty", selectedMarket);
  }, [selectedMarket]);

  useEffect(() => {
    setSelectData([]);
    const filters = {
      status: selectStatus,
      market: selectedMarket,
      date: dateRange,
      acno: searchText,
    };

    localStorage.setItem("warrantyFilters", JSON.stringify(filters));
  }, [selectedMarket, dateRange, searchText, selectStatus]);

  if (userProgramsLoading) {
    return <Spinner />;
  }

  if (
    userPrograms &&
    !userPrograms.wf_adminFirst_afUserProgram?.edges?.find(
      (item) => item.node.afProgram?.programName === "Warranty"
    )
  ) {
    return <NoPermission />;
  }

  return (
    <div id="warrantyWrapper">
      <ContentTitle title="Warranty" />
      <div className="content-container">
        <div
          className={"flex justify-between"}
        >
          <Dropdown
            menu={{
              items: statusMenuItems,
              onClick: (e: any) => setSelectStatus(e.key)
            }}
          >
            <Space>
              {trans("common:common.status") + " : " + trans(`clubApp:warranty.${selectStatus.replace(' ', '')}`)}
              <DownOutlined />
            </Space>
          </Dropdown>
          <MarketDropdown
            selectedMarket={selectedMarket}
            setSelectedMarket={setSelectedMarket}
            includeJp={true}
          />
        </div>
        <Divider />
        <div
          className={"flex mb-5 justify-between"}
        >
          <div className="flex justify-start">
            <RangePicker
              className="date-range-picker"
              style={{ width: 230, height: 32 }}
              format="YYYY-MM-DD"
              value={
                dateRange && dateRange[0] && dateRange[1]
                  ? [dayjs(dateRange[0]), dayjs(dateRange[1])]
                  : [null, null]
              }
              onChange={(dates, dateStrings) => {
                setDateRange(dateStrings as [string, string]);
              }}
              disabledDate={(current) => {
                if (!current) return false;
                return (
                  current.isAfter(dayjs(), "day") ||
                  current.isBefore(dayjs().subtract(1, "year"), "day")
                );
              }}
            />
            <Search
              allowClear
              className="mx-4"
              placeholder="Search by application number, ACNO"
              style={{ width: 325 }}
              value={searchBoxText}
              onChange={(e) => setSearchBoxText(e.target.value)}
              onSearch={onSearch}
            />
          </div>
          <div className="flex justify-end" style={{ width: 800 }} >
            {selectData.length > 0 &&
              <span className="mx-2" style={{ alignContent: "center" }}>{trans("clubApp:warranty.itemsSelected", { count: selectData.length })}</span>
            }
            {selectStatus === StatusTypes.Awaiting &&
              <Button
                type="primary"
                disabled={selectData.length === 0}
                onClick={handleCompleteClick}
              >
                {trans("clubApp:warranty.CompleteChecking")}
              </Button>
            }
            <Button
              className="mx-2"
              disabled={selectData.length === 0}
              onClick={handleExportClick}
            >
              {trans("common:button.export")}
            </Button>
            <WarrantyPrintButton
              applicationIds={selectData}
              buttonText={trans("common:button.print")}
              type="default"
              market={selectedMarket}
              disabled={selectData.length === 0}
            />
          </div>
        </div>
        <div className={"flex mb-5 justify-between"} style={{ marginTop: -12 }}>
          <div className="flex justify-start">
            <Button type="default" onClick={handleResetFiltersDates}>
              {trans("clubApp:warranty.resetDates")}
            </Button>
          </div>
        </div>
        <TableCustom
          columns={createColumns(
            handleDetailClick,
            handleCheckBoxTitleChange,
            handleCheckBoxRowChange,
            isAllSelect,
            selectData,
            trans
          )}
          rowClassName={(record: WarrantyListResponse) =>
            selectData.includes(record.applicationId)
              ? "selected-row"
              : ""
          }
          dataSource={warrantyList}
          rowKey="applicationId"
          scroll={{ x: "max-content", y: 425 }}
          locale={{
            emptyText: hasSearchCondition ? (
              <Empty
                description={trans("clubApp:warranty.noSearchResults")}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <Empty
                description={trans("clubApp:warranty.noSearchCriteria")}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button type="default" onClick={handleResetFiltersToday}>
                  {trans("clubApp:warranty.backToTodayRecord")}
                </Button>
              </Empty>
            )
          }}
        />
      </div>
      <GeneralModal
        open={showCompleteModal}
        close={handleCompleteClose}
        handleConfirm={handleConfirmClick}
        isDeletion={false}
        isConfirm={true}
        confirmBtnText={trans("common:common.yes")}
        width={417}
        title={trans("clubApp:warranty.confirmBulkCompleteChecking")}
        text={trans("clubApp:warranty.actionCannotBeUndone")}
        icon={<img
          alt="warning circle icon"
          src={require("../../../assets/icons/blue-warning-circle.png")}
          style={{ width: 22, height: 22, marginRight: 16 }}
        />}
      />
    </div >
  );
};

export default Warranty;
